import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { KpiCard, PageHeader, StatusPill } from "@/components/common";
import { ArrowLeft, FileText, ExternalLink, ShieldCheck, AlertOctagon, LifeBuoy, Activity } from "lucide-react";
import DataList from "@/components/DataList";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ──────────────────────────────────────────────────────────────────────────────
// Admin Overview — KPIs + operational queue + recent activity
// ──────────────────────────────────────────────────────────────────────────────
export function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [verif, setVerif] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [disputes, setDisputes] = useState([]);

  useEffect(() => {
    api.get("/stats").then(r => setStats(r.data));
    api.get("/admin/verifications?status=pending").then(r => setVerif(r.data));
    api.get("/tickets").then(r => setTickets((r.data || []).filter(t => t.status === "open" || t.status === "in_progress")));
    api.get("/admin/orders").then(r => setDisputes((r.data || []).filter(o => o.status === "disputed")));
  }, []);

  return (
    <>
      <PageHeader title="Platform Overview" subtitle="Marketplace activity, operational queues, and order flow at a glance." />

      {/* KPI strip */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 28 }}>
        <KpiCard label="Buyers" value={stats?.buyers ?? "—"} />
        <KpiCard label="Suppliers" value={stats?.suppliers ?? "—"} />
        <KpiCard label="RFQs" value={stats?.rfqs ?? "—"} />
        <KpiCard label="Quotes" value={stats?.quotes ?? "—"} />
        <KpiCard label="Orders" value={stats?.orders ?? "—"} />
      </div>

      {/* Operational queue — what the admin should do NEXT */}
      <div style={{ marginBottom: 12 }}>
        <div className="font-mono" style={{ fontSize: 11, letterSpacing: ".1em", color: "var(--amber-400)" }}>OPERATIONAL QUEUE</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14, marginBottom: 28 }}>
        <QueueCard
          icon={ShieldCheck}
          label="Pending Verifications"
          value={verif.length}
          ctaTo="/app/verifications"
          ctaLabel="Review queue →"
          tone={verif.length > 0 ? "warn" : "muted"}
          testId="queue-verifications"
        />
        <QueueCard
          icon={LifeBuoy}
          label="Open Tickets"
          value={tickets.length}
          ctaTo="/app/tickets"
          ctaLabel="Open inbox →"
          tone={tickets.filter(t => t.priority === "urgent").length > 0 ? "alert" : tickets.length > 0 ? "warn" : "muted"}
          testId="queue-tickets"
          subtitle={tickets.filter(t => t.priority === "urgent").length > 0
            ? `${tickets.filter(t => t.priority === "urgent").length} urgent`
            : null}
        />
        <QueueCard
          icon={AlertOctagon}
          label="Disputed Orders"
          value={disputes.length}
          ctaTo="/app/all-orders?status=disputed"
          ctaLabel="Review →"
          tone={disputes.length > 0 ? "alert" : "muted"}
          testId="queue-disputes"
        />
      </div>

      {/* Recent activity preview */}
      <RecentActivity />
    </>
  );
}

function QueueCard({ icon: Icon, label, value, ctaTo, ctaLabel, tone, testId, subtitle }) {
  const colors = {
    warn:  { border: "rgba(245,158,11,.35)", bg: "rgba(245,158,11,.04)", num: "var(--amber-400)" },
    alert: { border: "rgba(239,68,68,.35)",  bg: "rgba(239,68,68,.04)",  num: "var(--red)" },
    muted: { border: "var(--line)",          bg: "rgba(255,255,255,.02)", num: "var(--ink)" },
  }[tone];
  return (
    <div data-testid={testId} className="card" style={{ padding: 18, border: `1px solid ${colors.border}`, background: colors.bg, display: "grid", gap: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div className="font-mono" style={{ fontSize: 11, letterSpacing: ".08em", color: "var(--ink-mute)", textTransform: "uppercase", display: "inline-flex", gap: 6, alignItems: "center" }}>
          <Icon size={12} /> {label}
        </div>
      </div>
      <div className="font-serif-display" style={{ fontSize: 38, fontWeight: 600, color: colors.num, lineHeight: 1 }}>{value}</div>
      {subtitle && <div style={{ fontSize: 12, color: "var(--red)" }}>{subtitle}</div>}
      <Link to={ctaTo} className="btn btn-ghost" style={{ padding: "6px 12px", fontSize: 12, justifySelf: "start" }}>{ctaLabel}</Link>
    </div>
  );
}

function RecentActivity() {
  const [feed, setFeed] = useState([]);
  useEffect(() => {
    Promise.all([
      api.get("/admin/rfqs").catch(() => ({ data: [] })),
      api.get("/admin/orders").catch(() => ({ data: [] })),
    ]).then(([rfqs, orders]) => {
      const items = [
        ...(rfqs.data || []).slice(0, 8).map(r => ({
          when: r.created_at, kind: "rfq",
          text: <>RFQ <span className="font-mono" style={{ color: "var(--amber-400)" }}>{r.code}</span> · {r.product_name} <StatusPill status={r.status} /></>,
          href: `/app/rfqs/${r.id}`,
        })),
        ...(orders.data || []).slice(0, 8).map(o => ({
          when: o.created_at, kind: "order",
          text: <>Order <span className="font-mono" style={{ color: "var(--amber-400)" }}>{o.code}</span> · ${o.total_price?.toFixed(2)} <StatusPill status={o.status} /></>,
          href: `/app/orders/${o.id}`,
        })),
      ];
      items.sort((a, b) => new Date(b.when) - new Date(a.when));
      setFeed(items.slice(0, 12));
    });
  }, []);
  if (!feed.length) return null;
  return (
    <>
      <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
        <div className="font-mono" style={{ fontSize: 11, letterSpacing: ".1em", color: "var(--amber-400)" }}>RECENT ACTIVITY</div>
      </div>
      <div data-testid="recent-activity" className="card" style={{ padding: 0 }}>
        {feed.map((it, i) => (
          <Link key={i} to={it.href} style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderBottom: i < feed.length - 1 ? "1px solid var(--line)" : "none", textDecoration: "none", color: "var(--ink)", fontSize: 13 }}>
            <Activity size={12} color="var(--ink-mute)" />
            <span style={{ flex: 1 }}>{it.text}</span>
            <span className="font-mono" style={{ fontSize: 11, color: "var(--ink-mute)" }}>{new Date(it.when).toLocaleString()}</span>
          </Link>
        ))}
      </div>
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// AdminVerifications — DataList-powered with search + chips
// ──────────────────────────────────────────────────────────────────────────────
export function AdminVerifications() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/verifications").then(r => setRows(r.data)); }, []);

  const columns = [
    { key: "company", label: "Company",
      render: c => <><div style={{ fontWeight: 600 }}>{c.legal_name}</div>
                    <div className="font-mono" style={{ fontSize: 11, color: "var(--ink-mute)" }}>{c.tax_number || "—"}</div></>,
      search: c => `${c.legal_name} ${c.tax_number || ""} ${c.display_name || ""}` },
    { key: "role", label: "Role", render: c => <span style={{ color: "var(--ink-dim)" }}>{c.role}</span>, search: c => c.role },
    { key: "city", label: "City", render: c => c.city || "—", search: c => c.city || "" },
    { key: "categories", label: "Categories", render: c => <span style={{ fontSize: 12, color: "var(--ink-dim)" }}>{(c.categories || []).slice(0, 3).join(", ") || "—"}</span>, search: c => (c.categories || []).join(" ") },
    { key: "status", label: "Status", render: c => <StatusPill status={c.verification_status} /> },
    { key: "actions", label: "",
      render: c => <Link to={`/app/verifications/${c.id}`} data-testid={`review-${c.id}`} className="btn btn-primary" style={{ padding: "5px 12px", fontSize: 11 }}>Review →</Link> },
  ];

  const filters = [
    { key: "status", label: "Verification status",
      options: [
        { value: "all", label: "ALL" },
        { value: "pending", label: "PENDING" },
        { value: "needs_info", label: "NEEDS INFO" },
        { value: "approved", label: "APPROVED" },
        { value: "rejected", label: "REJECTED" },
      ],
      match: (row, value) => value === "all" || row.verification_status === value },
    { key: "role", label: "Role",
      options: [
        { value: "all", label: "ALL" },
        { value: "buyer", label: "BUYERS" },
        { value: "supplier", label: "SUPPLIERS" },
      ],
      match: (row, value) => value === "all" || row.role === value },
  ];

  return (
    <>
      <PageHeader title="Company Verifications" subtitle="Click any company to review full registration details and supporting documents before approving." />
      <DataList
        testId="verifications-list"
        rows={rows}
        columns={columns}
        filters={filters}
        emptyText="No companies match the current filters."
        searchPlaceholder="Search by legal name, tax #, or category…"
      />
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// AdminVerificationDetail — unchanged structurally, just modal-sheet for prompts
// ──────────────────────────────────────────────────────────────────────────────
export function AdminVerificationDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const API_BASE = BACKEND_URL || "";

  const load = () => api.get(`/admin/verifications/${id}`).then(r => setData(r.data)).catch(e => setErr(e.message));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const decide = async (decision) => {
    const reason = (decision === "rejected" || decision === "needs_info")
      ? window.prompt(`Reason for ${decision}:`) : "Approved";
    if (decision !== "approved" && !reason) return;
    setBusy(true);
    try {
      await api.post(`/admin/verifications/${id}`, { decision, reason });
      await load();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  if (err) return <div style={{ color: "var(--red)" }}>{err}</div>;
  if (!data) return <div style={{ color: "var(--ink-mute)" }}>Loading…</div>;
  const { company: c, owner, members, uploads, activity } = data;

  const Row = ({ label, value }) => (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(140px, 30%) 1fr", gap: 16, padding: "8px 0", borderBottom: "1px dashed var(--line)" }}>
      <div className="font-mono" style={{ fontSize: 11, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--ink-mute)" }}>{label}</div>
      <div style={{ color: "var(--ink)", fontSize: 14, wordBreak: "break-word" }}>{value || <span style={{ color: "var(--ink-mute)" }}>—</span>}</div>
    </div>
  );

  return (
    <>
      <button onClick={() => nav("/app/verifications")} className="btn btn-ghost" style={{ marginBottom: 14, fontSize: 12 }} data-testid="back-to-list">
        <ArrowLeft size={14} /> Back to verifications
      </button>
      <PageHeader
        title={c.legal_name}
        subtitle={<span className="font-mono" style={{ fontSize: 11, color: "var(--ink-mute)" }}>{c.id} · {c.role.toUpperCase()} · <StatusPill status={c.verification_status} /></span>}
      />

      <div className="card panel-action-row" style={{ padding: 16, marginBottom: 20, alignItems: "center" }}>
        <div style={{ flex: 1, minWidth: 200, fontSize: 13, color: "var(--ink-dim)" }}>
          Review every detail and any uploaded credentials below before deciding.
        </div>
        <button data-testid="approve-btn" disabled={busy} onClick={() => decide("approved")} className="btn btn-primary">Approve</button>
        <button disabled={busy} onClick={() => decide("needs_info")} className="btn btn-ghost">Request more info</button>
        <button disabled={busy} onClick={() => decide("rejected")} className="btn btn-danger">Reject</button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginBottom: 20 }}>
        <KpiCard label="Products" value={activity.products} />
        <KpiCard label="RFQs (buyer)" value={activity.rfqs_buyer} />
        <KpiCard label="Quotes (supplier)" value={activity.quotes_supplier} />
        <KpiCard label="Orders" value={activity.orders_buyer + activity.orders_supplier} />
      </div>

      <div className="card" style={{ padding: 20, marginBottom: 20 }}>
        <div className="font-mono" style={{ fontSize: 11, letterSpacing: ".1em", color: "var(--amber-400)", marginBottom: 12 }}>BUSINESS DETAILS</div>
        <Row label="Legal name" value={c.legal_name} />
        <Row label="Display name" value={c.display_name} />
        <Row label="Role" value={c.role} />
        <Row label="Tax number" value={c.tax_number} />
        <Row label="Categories" value={(c.categories || []).join(", ")} />
        <Row label="Service regions" value={(c.service_regions || []).join(", ")} />
      </div>

      {c.role === "supplier" && (
        <div className="card" style={{ padding: 20, marginBottom: 20 }}>
          <div className="font-mono" style={{ fontSize: 11, letterSpacing: ".1em", color: "var(--amber-400)", marginBottom: 12 }}>SUPPLIER CAPABILITIES</div>
          <Row label="About" value={c.about} />
          <Row label="Business type" value={c.business_type} />
          <Row label="Years in business" value={c.years_in_business} />
          <Row label="Team size" value={c.team_size} />
          <Row label="Default lead time (days)" value={c.lead_time_days_default} />
          <Row label="Minimum order qty" value={c.moq} />
          <Row label="Website" value={c.website} />
          <Row label="Certifications" value={(c.certifications || []).join(", ")} />
          <Row label="Brands carried" value={(c.brands || []).join(", ")} />
          <Row label="Sub-specialities" value={(c.subcategories || []).join(", ")} />
        </div>
      )}

      <div className="card" style={{ padding: 20, marginBottom: 20 }}>
        <div className="font-mono" style={{ fontSize: 11, letterSpacing: ".1em", color: "var(--amber-400)", marginBottom: 12 }}>CONTACT &amp; ADDRESSES</div>
        <Row label="Address" value={c.address} />
        <Row label="City" value={c.city} />
        <Row label="Province" value={c.province} />
        <Row label="Postal code" value={c.postal_code} />
        <Row label="Phone" value={c.phone} />
        <Row label="Warehouse address" value={c.warehouse_address} />
        <Row label="Bank details" value={c.bank_details} />
      </div>

      <div className="card" style={{ padding: 20, marginBottom: 20 }}>
        <div className="font-mono" style={{ fontSize: 11, letterSpacing: ".1em", color: "var(--amber-400)", marginBottom: 12 }}>ACCOUNT OWNER</div>
        {owner ? (
          <>
            <Row label="Name" value={owner.full_name} />
            <Row label="Email" value={owner.email} />
            <Row label="Role" value={owner.role} />
            <Row label="Created" value={owner.created_at && new Date(owner.created_at).toLocaleString()} />
          </>
        ) : <div style={{ color: "var(--ink-mute)" }}>No owner found.</div>}
        {members && members.length > 0 && (
          <>
            <div className="font-mono" style={{ fontSize: 11, letterSpacing: ".1em", color: "var(--ink-mute)", marginTop: 16, marginBottom: 8 }}>OTHER MEMBERS · {members.length}</div>
            {members.map(m => (
              <div key={m.id} style={{ fontSize: 13, color: "var(--ink-dim)", padding: "4px 0" }}>{m.full_name} · {m.email}</div>
            ))}
          </>
        )}
      </div>

      <div className="card" style={{ padding: 20 }}>
        <div className="font-mono" style={{ fontSize: 11, letterSpacing: ".1em", color: "var(--amber-400)", marginBottom: 12 }}>UPLOADED DOCUMENTS · {uploads?.length || 0}</div>
        {(!uploads || uploads.length === 0) ? (
          <div style={{ color: "var(--ink-mute)", fontSize: 13 }}>No documents uploaded yet. You may "Request more info" to ask the company to attach credentials.</div>
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            {uploads.map(u => (
              <a key={u.id} href={`${API_BASE}${u.url}`} target="_blank" rel="noreferrer"
                 data-testid={`upload-${u.id}`}
                 style={{ display: "flex", alignItems: "center", gap: 12, padding: 12,
                          background: "var(--navy-900)", border: "1px solid var(--line)",
                          borderRadius: 4, textDecoration: "none", color: "var(--ink)" }}>
                <FileText size={20} color="var(--amber-500)" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.filename}</div>
                  <div className="font-mono" style={{ fontSize: 11, color: "var(--ink-mute)" }}>
                    {u.purpose} · {(u.size / 1024).toFixed(1)} KB · {u.mime_type} · {new Date(u.created_at).toLocaleDateString()}
                  </div>
                </div>
                <ExternalLink size={16} color="var(--ink-mute)" />
              </a>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// AdminAllRfqs — DataList-powered
// ──────────────────────────────────────────────────────────────────────────────
export function AdminAllRfqs() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/rfqs").then(r => setRows(r.data)); }, []);

  const columns = [
    { key: "code", label: "Code",
      render: r => <Link to={`/app/rfqs/${r.id}`} data-testid={`admin-rfq-link-${r.id}`} className="font-mono" style={{ color: "var(--amber-400)" }}>{r.code}</Link>,
      search: r => r.code },
    { key: "product", label: "Product", render: r => r.product_name, search: r => r.product_name },
    { key: "category", label: "Category", render: r => <span style={{ color: "var(--ink-dim)", fontSize: 13 }}>{r.category}</span>, search: r => r.category },
    { key: "qty", label: "Qty", render: r => r.quantity },
    { key: "status", label: "Status", render: r => <StatusPill status={r.status} /> },
    { key: "matched", label: "Matched", render: r => r.matched_supplier_ids?.length || 0 },
    { key: "created", label: "Created", render: r => <span className="font-mono" style={{ fontSize: 12, color: "var(--ink-mute)" }}>{new Date(r.created_at).toLocaleDateString()}</span> },
    { key: "actions", label: "", render: r => <Link to={`/app/rfqs/${r.id}`} data-testid={`admin-rfq-open-${r.id}`} className="btn btn-primary" style={{ padding: "5px 12px", fontSize: 11 }}>Open →</Link> },
  ];

  const filters = [
    { key: "status", label: "Status",
      options: [
        { value: "all", label: "ALL" },
        { value: "open", label: "OPEN" },
        { value: "matched", label: "MATCHED" },
        { value: "quoted", label: "QUOTED" },
        { value: "awarded", label: "AWARDED" },
        { value: "expired", label: "EXPIRED" },
      ],
      match: (row, value) => value === "all" || row.status === value },
  ];

  return (
    <>
      <PageHeader title="All RFQs" subtitle="Platform-wide RFQ oversight. Click any RFQ to open its full detail." />
      <DataList
        testId="admin-rfqs"
        rows={rows}
        columns={columns}
        filters={filters}
        emptyText="No RFQs match the current filters."
        searchPlaceholder="Search by code, product, or category…"
      />
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// AdminAllOrders — DataList-powered
// ──────────────────────────────────────────────────────────────────────────────
export function AdminAllOrders() {
  const [rows, setRows] = useState([]);
  const [searchParams] = useSearchParams();
  const initialStatus = searchParams.get("status") || "all";
  useEffect(() => { api.get("/admin/orders").then(r => setRows(r.data)); }, []);

  const columns = [
    { key: "code", label: "PO",
      render: o => <Link to={`/app/orders/${o.id}`} data-testid={`admin-order-link-${o.id}`} className="font-mono" style={{ color: "var(--amber-400)" }}>{o.code}</Link>,
      search: o => o.code },
    { key: "product", label: "Product", render: o => o.product_name, search: o => o.product_name },
    { key: "qty", label: "Qty", render: o => o.quantity },
    { key: "total", label: "Total", render: o => <span style={{ fontWeight: 600 }}>${o.total_price.toFixed(2)}</span> },
    { key: "status", label: "Status", render: o => <StatusPill status={o.status} /> },
    { key: "created", label: "Created", render: o => <span className="font-mono" style={{ fontSize: 12, color: "var(--ink-mute)" }}>{new Date(o.created_at).toLocaleDateString()}</span> },
  ];

  const filters = [
    { key: "status", label: "Status",
      options: [
        { value: "all", label: "ALL" },
        { value: "pending_payment", label: "PENDING" },
        { value: "payment_received", label: "PAID" },
        { value: "processing", label: "PROCESSING" },
        { value: "shipped", label: "SHIPPED" },
        { value: "delivered", label: "DELIVERED" },
        { value: "closed", label: "CLOSED" },
        { value: "disputed", label: "DISPUTED" },
      ],
      match: (row, value) => value === "all" || row.status === value },
  ];

  return (
    <>
      <PageHeader title="All Orders" subtitle="Platform-wide order oversight." />
      <DataList
        testId="admin-orders"
        rows={rows}
        columns={columns}
        filters={filters}
        initialFilters={{ status: initialStatus }}
        emptyText="No orders match the current filters."
        searchPlaceholder="Search by PO or product…"
      />
    </>
  );
}
